import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  useTranslation,
  type TranslationKeys,
} from "@/hooks/useTranslation/useTranslation"
import { Info, TriangleAlert } from "lucide-react"
import type {
  CandidateArticle,
  CoveredRef,
  IssuerScopeProposal,
  ReaderHint,
  UncoveredClaim,
} from "../../../../../api/types"
import { inquiryNeeded } from "../inquiry/inquiryDraft"
import type { FrameworkEditionInfo } from "../mapping/editionInfo"
import type { Geltung } from "../mapping/geltungMapping"
import type { BrowserView } from "../scope/ArticleBrowser"
import { IssuerScopeCardNote } from "../scope/IssuerScopeNote"
import type { StatementCard } from "../useStudioV2State"
import { AskBox } from "./AskBox"
import { CountPill } from "./HeaderSentence"
import { R1Coachmark } from "./R1Coachmark"
import { ReaderHintNotes } from "./ReaderHintNotes"
import { cardHintArticles } from "./readerHintNote"
import { scopeFlagNote } from "./scopeFlagNote"

export interface ArticleRowProps {
  declId: string
  card: StatementCard
  pool: CandidateArticle[]
  /** The Geltung the card currently displays (`UnitCard`'s `displayGeltung`). */
  geltung: Geltung
  /** The reviewer picked "ask" LOCALLY on a card whose own scope is resolved:
   *  `UnitCard` mounts the R1 coachmark here, directly above the AskBox it
   *  explains (see `UnitCard`'s own doc on why never for a genuinely
   *  unresolved card). */
  forceCoachmark: boolean
  uncoveredClaimsByArticle?: Map<string, UncoveredClaim[]>
  /** The active Verordnung's inquiry template reader hints, keyed by article
   *  number (`useStudioV2State`'s `readerHintsByFramework`): the row notes
   *  every hint of the articles this card covers, none on a manually added
   *  card (`cardHintArticles`). Optional, defaults empty. */
  readerHintsByArticle?: Map<string, ReaderHint[]>
  /** Lands a reader hint's sheet row (1-based) in the workbook pane
   *  (`StudioV2Layout`'s own `handleJumpToSheetRow`). Omitted (a PDF): the
   *  hint lines stay plain notes. */
  onJumpToSheetRow?: (sheet: string, row: number) => void
  /** Opens the article browser in place of the cards (`ReviewMaskPane`'s
   *  sheet), from the count pill. Omitted: the pill stays a plain label. */
  onOpenArticleBrowser?: (view: BrowserView) => void
  /** The active Verordnung's Referenzlisten-Achse: the composer's draft
   *  names the reference list, or asks for it. Null: no catalogue. */
  editionInfo?: FrameworkEditionInfo | null
  /** The active Verordnung's display name (`StudioV2Layout`'s `nameOf`),
   *  threaded to AskBox's collector prefill (TUR-1211) so its draft
   *  paragraph names the same Verordnung the reviewer sees everywhere else.
   *  Falls back to the card's own raw framework slug: every caller predating
   *  this prop (and every test that renders `UnitCard` without it) keeps
   *  compiling, at the cost of a slug rather than a display name in that
   *  unlikely-to-render fallback text. */
  frameworkName?: string
  /** MC_ISSUER_SCOPE_PROPOSAL (TUR-1720): the declaration's issuer proposal,
   *  shown on a scope_all card only (`IssuerScopeCardNote`). Null/omitted
   *  with the flag off: no line at all. */
  issuerProposal?: IssuerScopeProposal | null
}

/**
 * The ARTIKEL row of an expanded unit card (review-mask prototype, round 4,
 * revised with Pablo 2026-09-16): one summary line, the count pill, the
 * article browser behind it, in EVERY state, and the Rückfrage composer
 * beneath it where the statement asks the supplier something
 * (`inquiryNeeded`: Geltung "Rückfrage" (`ask`), or an `unknown` verdict).
 * The pill stays in the Rückfrage state on purpose (the prototype dropped it
 * there): the primary use case is a follow-up that covers exactly the
 * articles no other statement covers, so the reviewer picks those articles
 * here and the paragraph then asks about them specifically. A statement
 * nothing is bound to yet reads "0 von 330 Artikeln ▾" (or "Unklare Artikel
 * ▾" under "Rückfrage") and the pill IS the way in (the browser carries the
 * searches its unresolved references suggest; asking is the header's
 * Geltung pick, excluding is the header's own X). The quiet notes (evidence
 * gap, uncovered claims, covered refs) stay under the pill's line, never in
 * the Rückfrage state (product decision, TUR-1211 wave 3).
 */
export function ArticleRow({
  declId,
  card,
  pool,
  geltung,
  forceCoachmark,
  uncoveredClaimsByArticle,
  readerHintsByArticle,
  onJumpToSheetRow,
  onOpenArticleBrowser,
  editionInfo = null,
  frameworkName,
  issuerProposal = null,
}: ArticleRowProps) {
  const { translate } = useTranslation()
  const total = pool.length
  const count = card.scopeAll ? total : card.articleNumbers.length
  const scopeUnclear = geltung === "ask"
  const uncoveredCount =
    geltung === "all" ? uncoveredClaimsCount(card, uncoveredClaimsByArticle) : 0

  const notes = (
    <>
      <EvidenceGapNote card={card} translate={translate} />
      {uncoveredCount > 0 && (
        <div className="sv2-artnote">
          <Info className="sv2-ic" />
          {translate("MC_DECL_V2_ARTS_UNCOVERED_CLAIM_COUNT", {
            n: uncoveredCount,
          })}
        </div>
      )}
      {/* Never for "ask": an unresolved statement already carries its
          own coachmark and AskBox, a second note would read as noise
          (product decision, TUR-1211 wave 3). */}
      <CoveredRefsNote coveredRefs={card.coveredRefs ?? []} />
      <ScopeFlagNotes card={card} />
      {card.scopeAll && (
        <IssuerScopeCardNote
          proposal={issuerProposal}
          onOpenArticleBrowser={onOpenArticleBrowser}
        />
      )}
      {readerHintsByArticle?.size ? (
        <ReaderHintNotes
          byArticle={readerHintsByArticle}
          articles={cardHintArticles(card, pool)}
          onJumpToRow={onJumpToSheetRow}
        />
      ) : null}
    </>
  )

  return (
    <div className="sv2-uslot sv2-dark" data-testid="article-row">
      <div className="sv2-slot">
        <span className="sv2-ski">
          {translate("MC_DECL_V2_UNIT_ARTICLES_LABEL")}
        </span>
        <div className="sv2-slotbody">
          <div className="sv2-slotline">
            <CountPill
              count={count}
              total={total}
              all={card.scopeAll}
              // Same reading as the header's pill: an unresolved scope with
              // nothing bound says "Unklare Artikel", never "0 von 330".
              unclear={scopeUnclear && !card.scopeAll && count === 0}
              title={
                onOpenArticleBrowser
                  ? translate("MC_DECL_V2_UNIT_SCOPE_PICK")
                  : undefined
              }
              onClick={
                onOpenArticleBrowser
                  ? () => onOpenArticleBrowser({ kind: "all" })
                  : undefined
              }
              translate={translate}
            />
            {(geltung === "group" || geltung === "material") &&
              card.scopeVerbatim && (
                <span className="sv2-hint truncate" title={card.scopeVerbatim}>
                  {translate("MC_DECL_V2_ARTS_PER_DOCUMENT", {
                    quote: card.scopeVerbatim,
                  })}
                </span>
              )}
          </div>
          {!scopeUnclear && notes}
          {inquiryNeeded(card, geltung) && (
            <>
              {forceCoachmark && <R1Coachmark derivation={null} force />}
              <AskBox
                declId={declId}
                framework={card.framework}
                statementId={card.id}
                scopeVerbatim={card.scopeVerbatim}
                poolNumbers={pool.map((a) => a.articleNumber)}
                card={card}
                geltung={geltung}
                frameworkName={frameworkName ?? card.framework}
                editionInfo={editionInfo}
              />
            </>
          )}
        </div>
      </div>
    </div>
  )
}

/** How many of this card's own resolved articles carry an advisory
 *  uncovered claim: the blanket ("all") card's own count note, since that
 *  reading renders no per-article rows to hang the marker on directly. */
function uncoveredClaimsCount(
  card: StatementCard,
  uncoveredClaimsByArticle?: Map<string, UncoveredClaim[]>,
): number {
  if (!uncoveredClaimsByArticle?.size) return 0
  return card.resolvedArticles.filter((n) => uncoveredClaimsByArticle.has(n))
    .length
}

/**
 * THE EVIDENCE-QUALITY WARNING, quiet amber, inside the ARTIKEL row. Renders
 * exactly when `card.evidenceGap` is set: the binder's blanket-seed carve-out
 * fired server-side (`_bind_one`'s `evidence_gap`, bind.py): the statement's
 * article set is already bound (a SYSTEM binding, `needs_input: null`), but
 * its seed clause never verified a quote in the document. Never a block:
 * `classifyScope` already reads `unexplainedCount` off `unexplained_articles`,
 * which this cell never appears in, only a nudge to check the original.
 */
function EvidenceGapNote({
  card,
  translate,
}: {
  card: StatementCard
  translate: (
    key: TranslationKeys,
    v?: Record<string, string | number>,
  ) => string
}) {
  if (!card.evidenceGap) return null
  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          {/* `tabIndex={0}`: a plain div is not focusable, which would make the
              tooltip mouse-only (Copilot, #2023). */}
          <div className="sv2-evgap" role="status" tabIndex={0}>
            <TriangleAlert className="sv2-ic" />
            <span>
              {translate("MC_DECL_V2_UNIT_EVIDENCE_GAP", {
                n: card.evidenceGap.articles,
              })}
            </span>
          </div>
        </TooltipTrigger>
        <TooltipContent side="top">
          {translate("MC_DECL_V2_UNIT_EVIDENCE_GAP_TITLE")}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  )
}

/** How many `text`s a covered-refs note lists before folding the rest into
 *  "und {k} weitere" / "and {k} more". */
const MAX_COVERED_REFS_SHOWN = 5

const COVERED_REF_RULE_KEY: Record<CoveredRef["rule"], TranslationKeys> = {
  identifier: "MC_DECL_V2_UNIT_COVERED_REF_RULE_IDENTIFIER",
  family: "MC_DECL_V2_UNIT_COVERED_REF_RULE_FAMILY",
}

/**
 * The resolver's own account of its work on this statement's scope: how many
 * references it was shown, which ones it could not place and why. Same quiet
 * register as `CoveredRefsNote` below, one line per flag, in the order the
 * resolver raised them (the backend appends per pass, so that order already
 * reads as "what happened first").
 *
 * NEVER renders the backend's own sentence. `scopeFlagNote` maps a flag's
 * KIND to this app's own copy and returns null for a kind this build does
 * not know, which renders nothing: a newer backend's flag stays silent
 * rather than putting an English developer sentence into a German interface.
 */
function ScopeFlagNotes({ card }: { card: StatementCard }) {
  const { translate } = useTranslation()
  const notes = (card.scopeFlags ?? [])
    .map(scopeFlagNote)
    .filter((note) => note !== null)
  if (notes.length === 0) return null
  return (
    <>
      {notes.map((note, i) => (
        <div className="sv2-artnote" key={i}>
          <Info className="sv2-ic" />
          {translate(note.key, note.values)}
        </div>
      ))}
    </>
  )
}

/**
 * TUR-1211 wave 3, product decision: a covered reference is a document
 * mention M2 already resolved to a live article, never a gap, so this
 * renders as the SAME quiet grey note family as the uncovered-claims count
 * above (`.sv2-artnote`, an `Info` icon), one line, under the count pill.
 *
 * `text` only, never `article_ids`: those are master-data article IDs, not
 * the display identity (`articleNumbers`) the rest of this card shows.
 */
function CoveredRefsNote({ coveredRefs }: { coveredRefs: CoveredRef[] }) {
  const { translate } = useTranslation()
  if (coveredRefs.length === 0) return null
  const shown = coveredRefs.slice(0, MAX_COVERED_REFS_SHOWN)
  const extra = coveredRefs.length - shown.length
  const list =
    shown.map((r) => r.text).join(", ") +
    (extra > 0
      ? translate("MC_DECL_V2_UNIT_COVERED_REFS_MORE", { k: extra })
      : "")
  const tooltip = shown
    .map((r) => `${r.text}: ${translate(COVERED_REF_RULE_KEY[r.rule])}`)
    .join("\n")
  return (
    <div className="sv2-artnote" title={tooltip}>
      <Info className="sv2-ic" />
      {translate(
        coveredRefs.length === 1
          ? "MC_DECL_V2_UNIT_COVERED_REF"
          : "MC_DECL_V2_UNIT_COVERED_REFS",
        { n: coveredRefs.length, list },
      )}
    </div>
  )
}
